import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { dueAtOn } from "../lib/lane-due";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "./ui/Button";
import { Overlay } from "./ui/Overlay";
import { cn } from "./ui/cn";
import type { TicketSummary } from "../types";

/**
 * Raise a Lane task from a ticket.
 *
 * Prefilled from the ticket, because the overwhelmingly common case is "this
 * conversation needs a piece of work doing" and retyping the subject is pure
 * friction.
 *
 * The board is a required, visible choice rather than a default: which board a
 * task belongs on is a real decision that depends on the work, and a hidden
 * default would quietly file everything into one place.
 */

/**
 * Boards whose work is time-critical enough that a task without a date is not
 * worth raising -- a courier claim or a return with no due date is one nobody
 * chases until the customer does.
 *
 * Matched on name because that is what a manager recognises and can change in
 * Lane; the trade-off is that renaming one of these boards silently drops the
 * requirement, which is why it reads as a list of names rather than ids.
 */
const DUE_DATE_REQUIRED_BOARDS = [
  "Courier Issues",
  "Order Issues",
  "Returns",
];
export function LaneTaskDialog({
  ticket,
  onClose,
}: {
  ticket: TicketSummary;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(ticket.subject);
  const [description, setDescription] = useState("");
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [dueAt, setDueAt] = useState("");
  const [boardId, setBoardId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: queryKeys.laneMembers,
    queryFn: api.laneMembers,
    staleTime: 5 * 60_000,
  });

  const boards = useQuery({
    queryKey: queryKeys.laneBoards,
    queryFn: api.laneBoards,
    staleTime: 5 * 60_000,
  });

  const statuses = useQuery({
    queryKey: ["lane-statuses", boardId],
    queryFn: () => api.laneStatuses(boardId),
    enabled: boardId !== "",
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const create = useMutation({
    mutationFn: () =>
      api.createLaneTask(ticket.id, {
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(assigneeIds.length ? { assigneeIds } : {}),
        /* The input is a date; Lane wants an instant, and the desk's deadline
           is four o'clock London on that date -- see `lane-due.ts`. */
        ...(dueAt ? { dueAt: dueAtOn(dueAt) } : {}),
        boardId,
        ...(statusId ? { statusId } : {}),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.laneLink(ticket.id), result);
      // The task writes the reference onto the ticket and posts a note, so both
      // the ticket and its conversation are now stale.
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticket(ticket.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      onClose();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not create task"),
  });

  const boardName =
    (boards.data ?? [])
      .flatMap((group) => group.boards)
      .find((board) => board.id === boardId)?.name ?? null;

  const dueRequired =
    boardName !== null && DUE_DATE_REQUIRED_BOARDS.includes(boardName);

  const canCreate =
    title.trim().length > 0 &&
    boardId !== "" &&
    assigneeIds.length > 0 &&
    (!dueRequired || dueAt !== "") &&
    !create.isPending;

  return (
    <Overlay
      title="New Lane task"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!canCreate}>
            {create.isPending ? "Creating…" : "Create task"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Title" required>
          <input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Description" hint="Optional.">
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            placeholder="What needs doing, and anything from this ticket worth carrying over."
            className={inputClass}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          {/*
            Required, not optional. An unassigned task lands on a board with
            nobody accountable for it, which is how a ticket gets closed
            against work that never actually gets picked up.
          */}
          <Field label="Assign to" required>
            <select
              value={assigneeIds[0] ?? ""}
              onChange={(event) =>
                setAssigneeIds(event.target.value ? [event.target.value] : [])
              }
              className={inputClass}
            >
              <option value="">Choose someone</option>
              {(members.data ?? []).map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Due"
            required={dueRequired}
            hint={dueRequired ? undefined : "Optional."}
          >
            <input
              type="date"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Board" required>
            <select
              value={boardId}
              onChange={(event) => {
                setBoardId(event.target.value);
                // The old column belongs to the old board.
                setStatusId("");
              }}
              className={inputClass}
            >
              <option value="">Choose a board</option>
              {(boards.data ?? []).map((group) => (
                <optgroup key={group.space.id} label={group.space.name}>
                  {group.boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <Field label="Column" hint="Defaults to the first.">
            <select
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              disabled={boardId === ""}
              className={inputClass}
            >
              <option value="">First column</option>
              {(statuses.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {assigneeIds.length === 0 ? (
          <p className="text-xs text-amber-600">
            Every task needs somebody to own it.
          </p>
        ) : null}

        {dueRequired && dueAt === "" ? (
          <p className="text-xs text-amber-600">
            {boardName} tasks need a due date.
          </p>
        ) : null}

        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    </Overlay>
  );
}

const inputClass = cn(
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
  "disabled:opacity-60",
);

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-1.5 text-xs text-muted">
        {label}
        {required ? <span className="text-brand-600">*</span> : null}
        {hint ? <span className="text-faint">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}
